import { useEffect, useState, useRef, useImperativeHandle, forwardRef, useCallback } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { Card, CardContent } from '@/components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { AnimatedShinyText } from '@/components/ui/animated-shiny-text'
import { Loader } from '@/components/ai-elements/loader'
import { SuccessCircleIcon, ErrorCircleIcon } from '@/components/icons'
import { cn } from '@/lib/utils'
import type { 
  APIBroadcastProgressProps, 
  APIProgressMessage
} from '@/types'

export interface APIBroadcastProgressRef {
  triggerCompletion: (success: boolean) => void
  forceCleanup: () => void // New method for immediate cleanup
}

export const APIBroadcastProgress = forwardRef<APIBroadcastProgressRef, APIBroadcastProgressProps>(({ 
  channelId, 
  onComplete,
  className 
}, ref) => {
  
  const [messages, setMessages] = useState<APIProgressMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  
  const channelRef = useRef<RealtimeChannel | null>(null)
  const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const removalTimeoutRef = useRef<NodeJS.Timeout | null>(null) // Separate timer for component removal

  // Function to close the channel
  const closeChannel = () => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
      channelRef.current = null
    }
  }

  // Function to cleanup all timers and resources
  const forceCleanup = useCallback(() => {
    
    if (completionTimeoutRef.current) {
      clearTimeout(completionTimeoutRef.current)
      completionTimeoutRef.current = null
    }
    
    if (removalTimeoutRef.current) {
      clearTimeout(removalTimeoutRef.current)  
      removalTimeoutRef.current = null
    }
    
    closeChannel()
    onComplete?.() // Remove component immediately
  }, [onComplete])

  // Function to handle completion (triggered by API response - deprecated, now using broadcast events)
  const handleCompletion = useCallback((success: boolean) => {
    // Deprecated path (kept for imperative API compatibility)
    void success
    
    setIsLoading(false)
    
    // Wait 5 seconds before closing channel to catch any remaining broadcasts
    completionTimeoutRef.current = setTimeout(() => {
      closeChannel()
    }, 5000)
    
    // Wait 20 seconds before removing component (separate from button re-enabling)
    removalTimeoutRef.current = setTimeout(() => {
      onComplete?.()
    }, 20000)
    
  }, [onComplete])

  // Expose the completion handler via useImperativeHandle
  useImperativeHandle(ref, () => ({
    triggerCompletion: handleCompletion,
    forceCleanup: forceCleanup
  }), [handleCompletion, forceCleanup])

  useEffect(() => {
    // Set auth for private channel (required for private broadcasts)
    supabase.realtime.setAuth().catch(console.error)
    
    // Create and configure the broadcast channel
    const channel = supabase.channel(channelId, {
      config: {
        broadcast: { self: false },
        private: true  // ADD THIS
      }
    })

    channelRef.current = channel

    // Listen for all broadcast events on this channel (progress, complete, and error for visual feedback)
    channel.on('broadcast', { event: '*' }, (payload: { event: string; payload?: { message?: string; color_hex?: string } }) => {
      
      const { event, payload: eventPayload } = payload
      
      // Handle progress, complete, and error events for visual feedback
      if (event === 'progress' || event === 'complete' || event === 'error') {
        const newMessage: APIProgressMessage = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          type: event as 'progress' | 'complete' | 'error',
          message: eventPayload?.message || 'Unknown message',
          timestamp: new Date(),
          color_hex: eventPayload?.color_hex
        }

        setMessages(prev => [...prev, newMessage])
        setIsLoading(false)
        
        // Schedule auto-removal after complete broadcast
        if (event === 'complete') {
          // Clear any existing removal timeout before setting a new one
          if (removalTimeoutRef.current) {
            clearTimeout(removalTimeoutRef.current)
          }
          
          removalTimeoutRef.current = setTimeout(() => {
            onComplete?.()
          }, 20000)
        }
      }
    })

    // Subscribe to the channel
    channel.subscribe()

    // Cleanup function
    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current)
      }
      
      if (removalTimeoutRef.current) {
        clearTimeout(removalTimeoutRef.current)
      }
      
      closeChannel()
    }
  }, [channelId])

  // Get the latest message for display
  const latestMessage = messages[messages.length - 1]
  
  // Determine visual state based on LATEST broadcast message (not all messages)
  const latestMessageType = latestMessage?.type
  const showLoader = isLoading && !latestMessageType
  const showBroadcastComplete = latestMessageType === 'complete'
  const showError = latestMessageType === 'error'
  const showProgress = latestMessageType === 'progress'
  
  // Dynamic title for accordion - use latest message or default
  const accordionTitle = latestMessage?.message || (showLoader ? 'Processing...' : 'Import progress')
  const accordionColorHex = latestMessage?.color_hex
  
  // Show animated shiny text for progress events (not complete nor error)
  const showAnimatedTitle = (showLoader || showProgress || (showBroadcastComplete && !showError))

  return (
    <Card className={cn("w-full max-w-md mx-auto border py-0 bg-background rounded-md", className)}>
      <CardContent className="p-0">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="progress" className="border-none">
            <AccordionTrigger className="p-2 hover:no-underline">
              <div className="flex items-center space-x-3">
                {/* Status indicator - based ONLY on broadcast messages */}
                <div className="flex-shrink-0">
                  {showLoader && (
                    <Loader size={16} />
                  )}
                  {showBroadcastComplete && (
                    <div className="text-primary">
                      <SuccessCircleIcon />
                    </div>
                  )}
                  {showError && (
                    <div className="text-destructive">
                      <ErrorCircleIcon />
                    </div>
                  )}
                  {showProgress && !showLoader && (
                    <Loader size={16} />
                  )}
                </div>

                {/* Color indicator from latest message */}
                {accordionColorHex && (
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0 border border-border"
                    style={{ backgroundColor: accordionColorHex }}
                  />
                )}

                {/* Dynamic title from latest broadcast message */}
                {showAnimatedTitle ? (
                  <AnimatedShinyText className={cn(
                    "text-sm font-normal truncate text-muted-foreground"
                  )}>
                    {accordionTitle}
                  </AnimatedShinyText>
                ) : (
                  <span className={cn(
                    "text-sm font-normal truncate",
                    showError && "text-[color:var(--destructive)]",
                    showBroadcastComplete && "text-green-600",
                    showProgress && "text-muted-foreground"
                  )}>
                    {accordionTitle}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            
            <AccordionContent className="px-6 pb-4">
              <div className="space-y-3">
                {/* Show all progress messages in chronological order */}
                {messages.length > 0 ? (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {messages.map((message) => (
                      <div 
                        key={message.id}
                        className="flex items-center gap-2"
                      >
                        {message.color_hex && (
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0 border border-border"
                            style={{ backgroundColor: message.color_hex }}
                          />
                        )}
                        <p 
                          className={cn(
                            "text-xs",
                            message.type === 'error' && "text-[color:var(--destructive)]",
                            message.type === 'complete' && "text-green-500",
                            message.type === 'progress' && "text-muted-foreground"
                          )}
                        >
                          {message.message}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : showLoader ? (
                  <p className="text-xs text-muted-foreground">
                    Waiting for updates...
                  </p>
                ) : null}

                {/* Auto-removal notice - only for completed state, not error */}
                {showBroadcastComplete && (
                  <p className="text-xs text-muted-foreground opacity-60">
                    This will disappear in 20 seconds...
                  </p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  )
})

APIBroadcastProgress.displayName = 'APIBroadcastProgress'

export default APIBroadcastProgress
